import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Chat,
  ChatMessageSendEvent,
  ChatMessageTemplateProps
} from '@progress/kendo-react-conversational-ui';

import {
  ChatBetweenPlayer,
  GetChatHistoryBetweeenPlayersAsync,
  PostChatBetweenPlayer
} from '../../../../../../services/players';
import {
  PlayerChatContext,
  PlayerChatContextProps
} from '../../../../../../contexts/player-chat';
import { PlayerState } from '../../../../../../types/state';
import { PlayerContext } from '../../../../../../contexts/player';
import MessageChat from '../../../../../../components/chat/message-chat';
import { ChatState } from '../../../../../../types/state/websocket/chat-state';
import { GameDocumentContext } from '../../../../../../contexts/game-document';
import { MessageTemplate } from '../../../../../../components/chat/message-template';
import { DisplayLanguageContext } from '../../../../../../contexts/display-languages';
import { generateTitleById } from '../../../../../../utils/game-document/display-languages';

const ChatTrade: React.FC<{ tradePlayer?: PlayerState }> = ({
  tradePlayer
}) => {
  const { gameCode } = useParams();

  const { chatHub, setNewChat } = React.useContext(
    PlayerChatContext
  ) as PlayerChatContextProps;
  const [player] = React.useContext(PlayerContext);
  const [gameDocumentstate] = React.useContext(GameDocumentContext);
  const [displayLanguageContext] = React.useContext(DisplayLanguageContext);

  const [messages, setMessages] = React.useState<ChatState[]>([]);

  const playerCode = player.playerState?.code ?? '';
  const user = {
    id: playerCode,
    avatarUrl: player?.playerState?.avatarImage
  };

  const renderMessageTemplate = React.useCallback(
    (props: ChatMessageTemplateProps) => (
      <MessageTemplate
        hideChatButton={true}
        currentUserCode={user.id!}
        {...props}
      />
    ),
    [user.id]
  );

  const addNewMessage = async (event: ChatMessageSendEvent) => {
    try {
      let payload: ChatBetweenPlayer = {
        fromPlayerCode: playerCode,
        toPlayerCode: tradePlayer?.code ?? '',
        message: event.message.text ?? ''
      };

      await PostChatBetweenPlayer(gameCode ?? '', payload);

      const newMessage = {
        author: {
          id: event.message.author.id,
          name: event.message.author.name,
          avatarUrl: event.message.author.avatarUrl
        },
        timestamp: event.message.timestamp,
        group: 'player-chat',
        text: event.message.text
      };
      setMessages((prev) => [...prev, newMessage]);

      setNewChat((prev) =>
        prev.filter(
          (c) =>
            !(c.group === 'player-chat' && c.author.id === tradePlayer?.code)
        )
      );
    } catch (error) {
      console.log('There is an error when sending chat: ', error);
    }
  };

  const getChatHistoryAsync = async (code: string) => {
    let newChats: ChatState[] = [];
    GetChatHistoryBetweeenPlayersAsync(gameCode!, playerCode, code).then(
      (response) => {
        if (response && Array.isArray(response)) {
          response.forEach((chat) => {
            newChats.push({
              author: {
                id: chat.playerCode,
                name: chat.playerName ?? '',
                avatarUrl: chat.playerAvatar ?? ''
              },
              timestamp: new Date(chat.sentDateUtc),
              group: 'player-chat',
              text: chat.message
            });
          });

          setMessages(newChats);
        }
      }
    );
  };

  React.useEffect(() => {
    if (!chatHub || !gameCode) return;

    chatHub.on('PrivateChatReceived', (data) => {
      const { senderCode, senderName, senderAvatar, message, sentDateUtc } =
        data;
      const chat = {
        author: {
          id: senderCode,
          name: senderName,
          avatarUrl: senderAvatar
        },
        timestamp: new Date(sentDateUtc),
        group: 'player-chat',
        text: message
      };
      setMessages((prev) => [...prev, chat]);
    });
  }, [chatHub, gameCode]);

  React.useEffect(() => {
    getChatHistoryAsync(tradePlayer?.code ?? '');
  }, [tradePlayer?.code]);

  return (
    <div
      style={{
        height: '30%'
      }}>
      <Chat
        className={`chat-container`}
        user={user}
        messages={messages}
        onMessageSend={addNewMessage}
        placeholder={
          generateTitleById(
            '2373ccd3-e73e-403d-bd99-7083f1512a96',
            gameDocumentstate,
            displayLanguageContext.displayLanguageSelected.resources!,
            'game'
          ) || 'Type a message...'
        }
        message={MessageChat}
        messageTemplate={renderMessageTemplate}
      />
    </div>
  );
};

export default ChatTrade;
